// @flow

import React from 'react';
import { connect } from 'react-redux';
import CarouselEllipsis from 'app/common/react/components/common/Carousel/CarouselEllipsis';
import PinTypeIdentifier from 'app/common/react/components/DeprecatedPinRep/PinTypeIdentifier/PinTypeIdentifier';
import toPascalCase from 'app/common/lib/util/toPascalCase';
import type { Videos } from 'app/packages/pinterest-flow-types/video';
import zIndices from 'app/common/react/components/DeprecatedPinRep/styles/zIndices';
import { Box } from 'gestalt';
import { getProductMerchant } from 'app/common/lib/CommerceUtil';

export type PinTypes =
  | 'oneTap'
  | 'video'
  | 'gif'
  | 'product'
  | 'recipe'
  | 'carousel'
  | 'story-pin'
  | 'embeddedVideo'
  | 'collage';

type Props = {
  componentType?: number,
  pin: {
    id: number,
    promoter?: {
      full_name: string,
    },
    rich_summary?: {
      aggregate_rating?: {
        best_rating?: string,
        rating_value: string,
        review_count: number,
      },
    },
    videos?: {
      video_list: Videos,
    },
  },
  pinId: string,
  types: Array<PinTypes>,
  viewParameter?: $Values<PCONST.thrift.ViewParameterType>,
  viewType?: $Values<PCONST.thrift.ViewType>,
};

type EligiblePinTypes = {|
  isCarousel: boolean,
  isEmbeddedVideo: boolean,
  isGif: boolean,
  isOneTap: boolean,
  isProduct: boolean,
  isRecipe: boolean,
  isStoryPin: boolean,
  isVideo: boolean,
|};

const getDefaultPinTypes = (): EligiblePinTypes => ({
  isCarousel: false,
  isEmbeddedVideo: false,
  isGif: false,
  isOneTap: false,
  isProduct: false,
  isRecipe: false,
  isStoryPin: false,
  isVideo: false,
});

export const getEligiblePinTypes = (types: Array<PinTypes>): EligiblePinTypes =>
  types.reduce((result, current) => {
    const keyName = current === 'story-pin' ? 'StoryPin' : toPascalCase(current);
    return {
      ...result,
      [`is${keyName}`]: true,
    };
  }, getDefaultPinTypes());

export const PinType = ({ pin, pinId, types }: Props) => {
  const { promoter } = pin;
  const {
    isStoryPin,
    isOneTap,
    isCarousel,
    isEmbeddedVideo,
    isRecipe,
    isProduct,
    isGif,
  } = getEligiblePinTypes(types);

  // WARNING: order here is very important as we only show one icon on a Pin.
  // If a Pin qualifies for multiple types, it will be shown the first one that
  // matches in this order. This is kept in sync with mobile

  if (isOneTap) {
    // for one-tap carousels, add the ellpsis above the one-tap arrow
    if (isCarousel) {
      return (
        <Box paddingY={1}>
          <Box
            position="relative"
            marginBottom={2}
            marginLeft={2}
            dangerouslySetInlineStyle={{ __style: { zIndex: zIndices.max } }}
          >
            <CarouselEllipsis pinId={pinId} variant="legacy" />
          </Box>
          <PinTypeIdentifier text={(promoter || {}).full_name} icon="arrow-up-right" />
        </Box>
      );
    }

    return (
      <Box paddingY={1}>
        <PinTypeIdentifier text={(promoter || {}).full_name} icon="arrow-up-right" />
      </Box>
    );
  }

  if (isCarousel) {
    return <CarouselEllipsis pinId={pinId} variant="legacy" />;
  }

  if (isGif) {
    return (
      <Box paddingY={1}>
        <PinTypeIdentifier icon="gif" />
      </Box>
    );
  }

  if (isEmbeddedVideo) {
    return (
      <Box paddingY={1}>
        <PinTypeIdentifier icon="play" />
      </Box>
    );
  }

  if (isProduct) {
    // Product Pins are those with a valid buyable product or those that
    // have a rich type of product.
    return (
      <Box paddingY={1}>
        <PinTypeIdentifier text={getProductMerchant(pin)} icon="tag" />
      </Box>
    );
  }
  if (isRecipe) {
    //be careful removing the ternary, it throws console errors on closeupbody if you return false instead of null
    return pin.rich_summary &&
      pin.rich_summary.aggregate_rating &&
      !!pin.rich_summary.aggregate_rating.review_count ? (
      <Box paddingY={1}>
        <PinTypeIdentifier
          text={pin.rich_summary.aggregate_rating.review_count.toString()}
          icon="star"
          rating={
            pin.rich_summary && pin.rich_summary.aggregate_rating
              ? pin.rich_summary.aggregate_rating.rating_value
              : ''
          }
          max_rating={
            pin.rich_summary && pin.rich_summary.aggregate_rating
              ? pin.rich_summary.aggregate_rating.best_rating
              : ''
          }
        />
      </Box>
    ) : null;
  }

  if (isStoryPin) {
    return (
      <Box paddingY={1}>
        <PinTypeIdentifier icon="story-pin" />
      </Box>
    );
  }

  return null;
};

const mapStateToProps = ({ pins }, ownProps) => {
  const { pinId, pin } = ownProps;

  return {
    pin: pins[pinId] || pin,
  };
};

// $FlowFixMe: new errors found from flow 0.96 upgrade
export default connect(mapStateToProps)(PinType);
